/**
 * CustomFoodForm — modal/dialog for creating or editing a custom food (T11.4).
 *
 * Accepts macros in per_100g or per_serving basis; the route normalises on save.
 * ED-safe: no diet/loss/burn language. No "calories" framing for restriction.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateCustomFood, useUpdateCustomFood } from '../hooks/useCustomFoods.js';

interface MacroValues {
  kcal: string;
  proteinG: string;
  carbsG: string;
  fatG: string;
  fiberG: string;
}

interface Props {
  /** When provided, the form is in edit mode. */
  editFood?: {
    id: string;
    name: string;
    brand: string | null;
    macrosPer100g: { kcal: number; proteinG: number; carbsG: number; fatG: number; fiberG: number };
    servingG: number | null;
  };
  onClose: () => void;
  onSaved?: () => void;
}

const EMPTY_MACROS: MacroValues = { kcal: '', proteinG: '', carbsG: '', fatG: '', fiberG: '' };

function parseMacro(v: string): number {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function CustomFoodForm({ editFood, onClose, onSaved }: Props) {
  const { t } = useTranslation('settings');
  const create = useCreateCustomFood();
  const update = useUpdateCustomFood();

  const [name, setName] = useState(editFood?.name ?? '');
  const [brand, setBrand] = useState(editFood?.brand ?? '');
  const [basis, setBasis] = useState<'per_100g' | 'per_serving'>(
    editFood?.servingG ? 'per_serving' : 'per_100g',
  );
  const [servingG, setServingG] = useState(editFood?.servingG?.toString() ?? '');
  const [macros, setMacros] = useState<MacroValues>(() => {
    if (!editFood) return EMPTY_MACROS;
    const m = editFood.macrosPer100g;
    return {
      kcal: String(m.kcal),
      proteinG: String(m.proteinG),
      carbsG: String(m.carbsG),
      fatG: String(m.fatG),
      fiberG: String(m.fiberG),
    };
  });
  const [error, setError] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const isBusy = create.isPending || update.isPending;

  function setMacro(key: keyof MacroValues, val: string) {
    setMacros((prev) => ({ ...prev, [key]: val }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t('customFood.nameRequired'));
      return;
    }
    if (basis === 'per_serving' && !servingG) {
      setError(t('customFood.servingGRequired'));
      return;
    }

    const parsed = {
      kcal: parseMacro(macros.kcal),
      proteinG: parseMacro(macros.proteinG),
      carbsG: parseMacro(macros.carbsG),
      fatG: parseMacro(macros.fatG),
      fiberG: parseMacro(macros.fiberG),
    };

    try {
      if (editFood) {
        await update.mutateAsync({
          id: editFood.id,
          name: name.trim(),
          brand: brand || null,
          macros: parsed,
        });
      } else {
        await create.mutateAsync({
          name: name.trim(),
          brand: brand || undefined,
          basis,
          servingG: basis === 'per_serving' ? Number(servingG) : undefined,
          macros: parsed,
        });
      }
      onSaved?.();
      onClose();
    } catch {
      setError(t('customFood.saveFailed'));
    }
  }

  function handleBackdropKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay dismisses on click/Escape
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="presentation"
      onClick={onClose}
      onKeyDown={handleBackdropKey}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-stone-900"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cf-form-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <h2 id="cf-form-title" className="mb-4 text-lg font-semibold text-ink dark:text-stone-100">
          {editFood ? t('customFood.editTitle') : t('customFood.createTitle')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Name */}
          <div>
            <label htmlFor="cf-name" className="mb-1 block text-xs font-medium text-ink-soft">
              {t('customFood.nameLabel')}
            </label>
            <input
              id="cf-name"
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-husk px-3 py-2 text-sm dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
              placeholder={t('customFood.namePlaceholder')}
              maxLength={200}
            />
          </div>

          {/* Brand (optional) */}
          <div>
            <label htmlFor="cf-brand" className="mb-1 block text-xs font-medium text-ink-soft">
              {t('customFood.brandLabel')}
            </label>
            <input
              id="cf-brand"
              type="text"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full rounded-lg border border-husk px-3 py-2 text-sm dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
              placeholder={t('customFood.brandPlaceholder')}
              maxLength={200}
            />
          </div>

          {/* Basis toggle (only on create) */}
          {!editFood && (
            <div className="flex gap-2">
              {(['per_100g', 'per_serving'] as const).map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBasis(b)}
                  className={`flex-1 rounded-lg border py-1.5 text-xs font-medium transition-colors ${
                    basis === b
                      ? 'border-leaf bg-leaf/10 text-leaf'
                      : 'border-husk text-ink-soft hover:border-leaf/50 dark:border-stone-700'
                  }`}
                >
                  {t(`customFood.basis.${b}`)}
                </button>
              ))}
            </div>
          )}

          {/* Serving size (when per_serving) */}
          {basis === 'per_serving' && !editFood && (
            <div>
              <label htmlFor="cf-serving" className="mb-1 block text-xs font-medium text-ink-soft">
                {t('customFood.servingGLabel')}
              </label>
              <input
                id="cf-serving"
                type="number"
                inputMode="decimal"
                value={servingG}
                onChange={(e) => setServingG(e.target.value)}
                className="w-full rounded-lg border border-husk px-3 py-2 text-sm dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
                placeholder="30"
                min={0.1}
              />
            </div>
          )}

          {/* Macros */}
          <p className="text-xs font-medium text-ink-soft">
            {basis === 'per_serving'
              ? t('customFood.macrosPerServing')
              : t('customFood.macroPer100g')}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {(
              [
                ['kcal', t('customFood.kcal')],
                ['proteinG', t('customFood.proteinG')],
                ['carbsG', t('customFood.carbsG')],
                ['fatG', t('customFood.fatG')],
                ['fiberG', t('customFood.fiberG')],
              ] as [keyof MacroValues, string][]
            ).map(([key, label]) => (
              <div key={key}>
                <label htmlFor={`cf-${key}`} className="mb-1 block text-xs text-ink-soft">
                  {label}
                </label>
                <input
                  id={`cf-${key}`}
                  type="number"
                  inputMode="decimal"
                  value={macros[key]}
                  onChange={(e) => setMacro(key, e.target.value)}
                  className="w-full rounded-lg border border-husk px-2 py-1.5 text-sm dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
                  min={0}
                  placeholder="0"
                />
              </div>
            ))}
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm text-ink-soft hover:bg-stone-100 dark:hover:bg-stone-800"
            >
              {t('customFood.cancel')}
            </button>
            <button
              type="submit"
              disabled={isBusy}
              className="rounded-lg bg-leaf px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {isBusy ? t('customFood.saving') : t('customFood.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
